import React, { useState } from 'react';
import {
	Breadcrumbs,
	BreadcrumbsText,
	Checkbox,
	Link,
	TextBlock,
	Heading,
	Button,
	Tabs,
	WithTheme, useTheme
} from 'neon-design-system';
import { Box, Grid, useMediaQuery } from '@material-ui/core';
import { baseUrls } from '../../../../utils/baseUrls.utils';
import { styles } from './ChannelSubscribersShared';
import AdminList from '../../../ui/admin/AdminList';
import StatCards from '../../../ui/StatCards';
import { ShortDashboardUser, ShortUser } from '../../../../shared/types';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { TextOverlapping } from '../../../ui/TextOverlapping';

type TABS = 'subscriptions' | 'noInsightsAccount' | 'unmatched' | 'existing';

enum TAB_TITLES {
	subscriptions = 'New subscribers',
	noInsightsAccount = 'No Insights account',
	existing = 'Existing subscribers',
	unmatched = 'No Neon account'
}

interface PremiumChannelSubscribersBulkAddProps {
	channel: {
		_id: string;
		name: string;
	};
	unmatched: string[];
	users: ShortUser[];
	neonUsers: ShortDashboardUser[];
	existing: ShortUser[];
	onCancel: () => void;
	onConfirm: (overwriteSubscribed: boolean) => void;
}

export const ChannelSubscribersBulkAdd = ({channel, unmatched, users, existing, neonUsers, onCancel, onConfirm}: PremiumChannelSubscribersBulkAddProps): JSX.Element => {
	const { theme } = useTheme();
	const classes = styles();
	const total = users.length + existing.length + neonUsers.length + unmatched.length;
	const [overwriteSubscribed, setOverwriteSubscribed] = useState(false);

	const [tab, setTab] = useState<TABS>('subscriptions');

	const cards = [
		{
			label: 'Email addresses uploaded',
			value: total
		},
		{
			label: TAB_TITLES.subscriptions,
			value: users.length
		},
		{
			label: TAB_TITLES.existing,
			value: existing.length
		},
		{
			label: TAB_TITLES.noInsightsAccount,
			value: neonUsers.length
		},
		{
			label: TAB_TITLES.unmatched,
			value: unmatched.length
		}
	]

	const tabs = [{
		value: 'subscriptions',
		title: TAB_TITLES.subscriptions
	}, {
		value: 'existing',
		title: TAB_TITLES.existing
	}, {
		value: 'noInsightsAccount',
		title: TAB_TITLES.noInsightsAccount
	}, {
		value: 'unmatched',
		title: TAB_TITLES.unmatched
	}]

	const usersCols: ColDef[] = [
		{
			field: 'name',
			cellRendererFramework: (props: ICellRendererParams) => <WithTheme>
				<Link target="_blank" to={baseUrls.publisher.users.edit.replace(':_id', props.data._id)}>
					<strong>{props.data.name}</strong>
				</Link>
			</WithTheme>
		},
		{
			field: 'company'
		},
		{
			field: 'email'
		}
	];

	const shortNeonUserId = useMediaQuery(theme.breakpoints.down(1600));

	const neonUsersCols: ColDef[] = [
		{
			field: 'name',
			valueFormatter: (p) => [p.data.firstName, p.data.lastName].filter(f => !!f).join(' ')
		},
		{
			field: 'company'
		},
		{
			field: 'auth0email',
			headerName: 'Email'
		},
		{
			field: 'neonUserId',
			width: 400,
			cellRendererFramework: (props: ICellRendererParams) => shortNeonUserId ?
				<TextOverlapping value={props.value} maxLength={50} maxWidth={350} /> : <>{props.value}</>
		}
	];

	const canConfirm = users.length > 0 || (overwriteSubscribed && existing.length > 0);

	const buttons = <Box display="flex">
		<Box mr={2}>
			<Button colorType="primary" onClick={() => onConfirm(overwriteSubscribed)} disabled={!canConfirm}>
				Confirm and add subscribers
			</Button>
		</Box>
		<Box>
			<Button colorType="grey" onClick={onCancel}>
				Cancel
			</Button>
		</Box>
	</Box>;

	return (
		<div className={classes.container}>
			<div className={classes.main}>
				<Box mt={2}>
					<Breadcrumbs>
						<Link to={baseUrls.publisher.premiumChannels.list}>Channels</Link>
						<Link to={baseUrls.publisher.premiumChannels.subscribersStats.replace(':_id', channel._id || '')}>{channel.name}</Link>
						<BreadcrumbsText>Add subscribers</BreadcrumbsText>
					</Breadcrumbs>
				</Box>
				<Grid container spacing={4}>
					<Grid item xs={12}>
						<Box mt={2}>
							<Heading styleName="title1">Review upload</Heading>
						</Box>
						<Box mt={1}>
							<TextBlock ink="muted">A total of <b>{total} email addresses</b> were
								identified in the CSV file,
								of which {users.length} can be added as new subscribers to the channel.</TextBlock>
						</Box>
					</Grid>
					<Grid item xs={12}>
						<StatCards
							items={cards}
						/>
					</Grid>
					<Grid item xs={12}>
						{buttons}
					</Grid>
					<Grid item xs={12}>
						<Tabs tabs={tabs} onChange={v => setTab(v as TABS)} value={tab}/>
					</Grid>
					{tab === 'subscriptions' &&
						<Grid item xs={12}>
							<Heading styleName="headline1">Insights users to be added as
								subscribers: {users.length}</Heading>
							<AdminList
								items={users}
								showAdd={false}
								loading={false}
								showCaption={false}
								showHeader={false}
								columns={usersCols}
								removeBottomOffset
							/>
						</Grid>}
					{tab === 'existing' &&
						<Grid item xs={12}>
							<Heading styleName="headline1">Insights users already subscribed to this channel: {existing.length}</Heading>
							<Box mt={1}>
								<Checkbox
									label="Overwrite subscribed users"
									value="overwriteSubscribed"
									checked={overwriteSubscribed}
									onChange={({target}) => setOverwriteSubscribed(target.checked)}
								/>
							</Box>
							<AdminList
								items={existing}
								showAdd={false}
								loading={false}
								showCaption={false}
								showHeader={false}
								columns={usersCols}
								removeBottomOffset
							/>
						</Grid>}
					{tab === 'noInsightsAccount' &&
						<Grid item xs={12}>
							<Heading styleName="headline1">Neon users without Insights account: {neonUsers.length}</Heading>
							<AdminList
								items={neonUsers}
								showAdd={false}
								loading={false}
								showCaption={false}
								showHeader={false}
								columns={neonUsersCols}
								removeBottomOffset
							/>
						</Grid>}
					{tab === 'unmatched' &&
						<Grid item xs={12}>
							<Heading styleName="headline1">Email addresses that didn’t match with Neon identities: {unmatched.length}</Heading>
							<AdminList
								items={unmatched.sort().map(email => ({email}))}
								showAdd={false}
								loading={false}
								showCaption={false}
								showHeader={false}
								columns={[{field: 'email'}]}
								removeBottomOffset
							/>
						</Grid>}
					<Grid item xs={12}>
						{buttons}
					</Grid>
				</Grid>
			</div>
		</div>
	);
};
